// ============================================
// FILE: src/components/DesktopHomeScreen.jsx
// Full-width desktop home page. Reuses the same data, helpers and
// handlers as the mobile HomeScreen — only the presentation differs.
// Rendered (instead of HomeScreen) when viewport >= 1024px on the Home tab.
// ============================================

import React, { useState, useEffect, useRef } from 'react';
import {
  Heart, Gift, Package, Plus,
  ShoppingCart, Flame, ChevronRight, ChevronLeft,
} from 'lucide-react';

import ProductDetailsModal from './modals/ProductDetailsModal';
import DesktopNavbar from './DesktopNavbar';
import DesktopFooter from './DesktopFooter';
import { banners, categoryIcons } from '../data/products';
import { getProductImage, resolveProductImageUrl } from '../utils/imageHelpers';
import { getFinalPrice } from '../utils/priceHelpers';
import { isCategoryVisible } from '../utils/categoryConfig';

const DesktopHomeScreen = ({
  searchQuery = '',
  setSearchQuery,
  setShowNotifications,
  setShowLocationModal,
  selectedLocation,
  currentBanner,
  setCurrentBanner,
  addToCart,
  setShowSubscription,
  products,
  filteredProducts,
  cart = [],
  getCartCount,
  getCartTotal,
  updateQuantity,
  openCheckout,
  userProfile = {},
  notifications = [],
  favorites = [],
  setShowFavorites,
  toggleFavorite,
  setActiveTab,
  selectedCategory = 'All',
  setSelectedCategory,
  categories: apiCategories = [],
  onCategoryClick,
  onOpenOffers,
  showToast,
  appSettings,
  handleLogout,
  featureSections = [],
  appTheme = 'blue',
  onNavigate,
  isAuthenticated = true,
  onLogin,
}) => {
  const navTo = onNavigate || setActiveTab;
  const favArray = Array.isArray(favorites) ? favorites : [];
  const isProductFavorited = (productId) => (
    favArray.some((item) => String(item?.id) === String(productId))
  );

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [quantitySelections, setQuantitySelections] = useState({});
  const [quantityLoading, setQuantityLoading] = useState({});
  const productsPerPage = 30;

  const productsSectionRef = useRef(null);

  // Auto-rotate banners (same cadence as mobile)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [setCurrentBanner]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  useEffect(() => {
    if (searchQuery && searchQuery.length > 0) {
      const timer = setTimeout(() => {
        productsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [searchQuery]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  const categories = apiCategories.length > 0
    ? ['All', ...apiCategories.map(c => c.name).filter(isCategoryVisible)]
    : ['All', ...new Set(products.map(p =>
        typeof p.category === 'object' && p.category?.name ? p.category.name : p.category || 'Uncategorized'
      ).filter(Boolean).filter(isCategoryVisible))];

  // ── Shared helpers (mirrors HomeScreen) ──
  const getWeightParts = (product) => {
    if (!product) return { value: null, unit: '' };
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const raw = String(product.weight).trim();
      const match = raw.match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
    }
    return { value: null, unit: product.unit || '' };
  };

  const isWeightUnit = (unit) => {
    if (!unit) return false;
    return ['kg', 'gram', 'g'].includes(String(unit).toLowerCase().trim());
  };

  const formatUnit = (unit) => {
    if (!unit) return '';
    const str = String(unit).trim();
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getQuantityOptions = (product) => {
    const { unit } = getWeightParts(product);
    return isWeightUnit(unit) ? [0.5, 1, 2, 3] : [1, 2, 3, 4];
  };

  const formatWeightLabel = (product) => {
    const { value, unit } = getWeightParts(product);
    if (value) return `${value}${unit ? ` ${unit}` : ''}`;
    return product.weight ? String(product.weight) : '';
  };

  const getSelectedQuantity = (productId) => quantitySelections[productId] || 1;
  const setSelectedQuantity = (productId, qty) => {
    setQuantitySelections((prev) => ({ ...prev, [productId]: qty }));
  };
  const handleAddToCartWithQuantity = (product) => {
    addToCart(product, getSelectedQuantity(product.id));
  };

  // ── Product card (shared markup, reused in feature rows + grid) ──
  const renderProductCard = (product, index = 0) => {
    const isFav = isProductFavorited(product.id);
    const hasDiscount = Number(product.sale_price) > 0 && Number(product.price) > 0 && Number(product.sale_price) < Number(product.price);
    const offPercent = hasDiscount
      ? Math.round(((Number(product.price) - Number(product.sale_price)) / Number(product.price)) * 100)
      : 0;
    return (
      <div key={product.id} className="rounded-2xl overflow-hidden relative product-card glass-card bg-white shadow-sm hover:shadow-xl transition-shadow"
        style={{ animationDelay: `${(index % 10) * 50}ms` }}>
        <div
          className="relative overflow-hidden h-48 cursor-pointer glass-image group"
          onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
        >
          {getProductImage(product, 'card')}
          {hasDiscount && (
            <span className="absolute top-3 left-3 text-xs bg-gradient-to-r from-amber-500 via-orange-500 to-pink-600 text-white px-2 py-0.5 rounded-full font-semibold shadow-sm ring-1 ring-white/70 badge-off">
              {offPercent}% OFF
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); if (typeof toggleFavorite === 'function') toggleFavorite(product); }}
            className="absolute top-3 right-3 p-2 bg-white rounded-full shadow hover:scale-105 transition-transform"
          >
            <Heart size={18} className={isFav ? 'text-pink-500' : 'text-gray-400'} fill={isFav ? 'currentColor' : 'none'} />
          </button>
        </div>

        <div className="p-3">
          <h3
            className="font-display font-semibold text-gray-800 text-sm mb-1 cursor-pointer hover:text-orange-500 transition-colors line-clamp-1"
            onClick={() => { setSelectedProduct(product); setShowProductDetails(true); }}
          >{product.name}</h3>
          <div className="flex items-center gap-2 mb-2">
            {product.weight && (
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">{formatWeightLabel(product)}</span>
            )}
            {!product.weight && product.fat && (
              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">{product.fat} fat</span>
            )}
            {!product.weight && !product.fat && product.brand && (
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">{product.brand}</span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-2">
            {product.sale_price && product.sale_price < product.price ? (
              <>
                <span className="text-sm font-extrabold text-green-600 drop-shadow-sm">₹{product.sale_price}</span>
                <span className="text-xs text-gray-400 line-through">₹{product.price}</span>
              </>
            ) : (
              <span className="text-sm font-extrabold text-emerald-600 drop-shadow-sm">₹{getFinalPrice(product)}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {getQuantityOptions(product).map((qty) => (
                <button
                  key={qty}
                  type="button"
                  onClick={() => setSelectedQuantity(product.id, qty)}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                    getSelectedQuantity(product.id) === qty
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300'
                  }`}
                >
                  {qty}
                </button>
              ))}
              {isWeightUnit(getWeightParts(product).unit) && (
                <span className="text-xs text-gray-600 ml-1 font-medium">Kg</span>
              )}
              {!isWeightUnit(getWeightParts(product).unit) && getWeightParts(product).unit && (
                <span className="text-xs text-gray-600 ml-1 font-medium">{formatUnit(getWeightParts(product).unit)}</span>
              )}
            </div>

            {(() => {
              const cartItem = cart.find(item => item.id === product.id);
              if (cartItem && cartItem.quantity > 0) {
                return (
                  <div className="w-full flex items-center justify-center gap-2 bg-orange-50 border border-orange-300 rounded-lg px-2 py-1 mt-1">
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-orange-300 text-orange-600 text-xl font-bold shadow-sm hover:bg-orange-50 focus:outline-none disabled:opacity-50"
                      disabled={quantityLoading[product.id] || cartItem.quantity <= 1}
                      onClick={async () => {
                        if (typeof updateQuantity === 'function' && cartItem.quantity > 1) {
                          setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                          await updateQuantity(product.id, -1);
                          setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                        }
                      }}
                    >
                      <span>&#8722;</span>
                    </button>
                    <span className="text-base font-bold text-orange-600 w-10 text-center select-none">
                      {quantityLoading[product.id] ? '...' : cartItem.quantity}
                    </span>
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-white border border-orange-300 text-orange-600 text-xl font-bold shadow-sm hover:bg-orange-50 focus:outline-none disabled:opacity-50"
                      disabled={!!quantityLoading[product.id]}
                      onClick={async () => {
                        if (typeof updateQuantity === 'function' && !quantityLoading[product.id]) {
                          setQuantityLoading(prev => ({ ...prev, [product.id]: true }));
                          await updateQuantity(product.id, 1);
                          setQuantityLoading(prev => ({ ...prev, [product.id]: false }));
                        }
                      }}
                    >
                      <span>&#43;</span>
                    </button>
                  </div>
                );
              }
              return (
                <button
                  onClick={() => handleAddToCartWithQuantity(product)}
                  className="w-full bg-orange-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-1"
                >
                  <Plus size={16} />
                  Add to Cart
                </button>
              );
            })()}
          </div>

          {Boolean(Number(product.is_subscribable)) && (
            <button
              onClick={() => setShowSubscription(product)}
              className="w-full bg-green-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors mt-2"
            >
              Subscribe &amp; Save
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ background: '#f7f7f7', color: '#1b1b1b', minHeight: '100vh' }}>

      {/* ── Shared sticky desktop navbar ── */}
      <DesktopNavbar
        appSettings={appSettings}
        userProfile={userProfile}
        activeTab="home"
        setActiveTab={navTo}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedLocation={selectedLocation}
        setShowLocationModal={setShowLocationModal}
        notifications={notifications}
        favorites={favorites}
        getCartCount={getCartCount}
        setShowNotifications={setShowNotifications}
        setShowFavorites={setShowFavorites}
        handleLogout={handleLogout}
        isAuthenticated={isAuthenticated}
        onLogin={onLogin}
      />

      <main className="max-w-[1280px] mx-auto px-8 py-6">
        {/* Greeting + quick action pills */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <p className="text-sm text-gray-500">
            {userProfile?.name
              ? <>Hey <span className="font-semibold text-gray-700">{userProfile.name}</span> 👋 — fresh picks &amp; daily savings</>
              : <>Fresh picks &amp; daily savings</>}
          </p>
          <div className="flex items-center gap-3">
            {[
              { icon: <Gift size={14} />, label: 'Offers', action: () => onOpenOffers && onOpenOffers('all') },
              { icon: <Package size={14} />, label: 'Track Order', action: () => setActiveTab && setActiveTab('orders') },
              { icon: <span className="text-xs">🔔</span>, label: 'Subscribe', action: () => setShowSubscription(true) },
            ].map(({ icon, label, action }) => (
              <button key={label} onClick={action}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full whitespace-nowrap text-sm font-medium text-gray-700 transition-all hover:bg-orange-50"
                style={{ background: '#fff', border: '1.5px solid #e8e8e8' }}
              >
                <span style={{ color: '#f05a23' }}>{icon}</span>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Banner Carousel (wide hero) ── */}
        <div className="mb-8 banner-carousel">
          <div className="relative overflow-hidden rounded-3xl shadow-lg">
            <div
              className="flex banner-slide"
              style={{ transform: `translateX(-${currentBanner * 100}%)`, transition: 'transform 0.5s ease-in-out' }}
            >
              {banners.map((banner) => (
                <div
                  key={banner.id}
                  className="min-w-full relative overflow-hidden flex items-center"
                  style={{ background: banner.bgStyle || `linear-gradient(135deg,#3b82f6,#2563eb)`, height: 230 }}
                >
                  <div className="absolute -top-12 -right-12 w-52 h-52 rounded-full bg-white/10 pointer-events-none" />
                  <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/10 pointer-events-none" />

                  <div className="flex-1 px-10 py-7 z-10" style={{ maxWidth: '58%' }}>
                    <span className="bg-white/25 backdrop-blur-sm text-white text-[11px] font-bold px-3 py-1 rounded-full inline-block mb-2 tracking-wide uppercase">
                      {banner.offer}
                    </span>
                    <h2 className="text-2xl xl:text-3xl font-extrabold text-white leading-tight mb-1.5">{banner.title}</h2>
                    <p className="text-white/80 text-sm mb-4 leading-snug max-w-md line-clamp-2">{banner.subtitle}</p>
                    <button
                      onClick={() => {
                        if (banner.images) setSelectedCategory && setSelectedCategory('Fruits');
                        setTimeout(() => productsSectionRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                      }}
                      className="bg-white text-sm font-bold px-5 py-2.5 rounded-xl shadow-lg hover:scale-105 active:scale-95 transition-transform"
                      style={{ color: '#f05a23' }}
                    >
                      Shop Now →
                    </button>
                  </div>

                  <div className="absolute right-0 top-0 bottom-0 z-10 flex items-center justify-center" style={{ width: '42%' }}>
                    {banner.images ? (
                      <div className="relative w-full h-full">
                        <img src={`${process.env.PUBLIC_URL}${banner.images[0]}`} alt=""
                          className="absolute object-contain" style={{ width: 110, height: 110, right: 36, top: 24, transform: 'rotate(10deg)', filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.35))' }} />
                        <img src={`${process.env.PUBLIC_URL}${banner.images[1]}`} alt=""
                          className="absolute object-contain" style={{ width: 92, height: 92, right: 104, top: 44, transform: 'rotate(-6deg)', filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.3))' }} />
                        <img src={`${process.env.PUBLIC_URL}${banner.images[2]}`} alt=""
                          className="absolute object-contain" style={{ width: 86, height: 86, right: 44, bottom: 28, transform: 'rotate(8deg)', filter: 'drop-shadow(0 8px 14px rgba(0,0,0,0.3))' }} />
                        <img src={`${process.env.PUBLIC_URL}${banner.images[3]}`} alt=""
                          className="absolute object-contain" style={{ width: 64, height: 64, right: 112, bottom: 22, transform: 'rotate(-12deg)', filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.25))' }} />
                      </div>
                    ) : (
                      <span style={{ fontSize: 110, lineHeight: 1, filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.25))' }}>
                        {banner.icon}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 flex gap-2">
              {banners.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentBanner(index)}
                  className={`h-2 rounded-full transition-all ${currentBanner === index ? 'bg-white w-8' : 'bg-white/50 w-2'}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── Shop by Category ── */}
        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800 font-display">Shop by Category</h2>
            <span className="text-sm text-gray-400">{categories.length - 1} types</span>
          </div>
          <div className="flex gap-5 overflow-x-auto pb-2 hide-scrollbar">
            {categories.map(category => {
              const isActive = selectedCategory === category;
              const catPalettes = ['#FF6B6B','#4ECDC4','#45B7D1','#96CEB4','#FFEAA7','#DDA0DD','#98D8C8','#F7DC6F','#BB8FCE','#85C1E9'];
              const idx = categories.indexOf(category);
              const circleColor = isActive ? '#f05a23' : catPalettes[idx % catPalettes.length];
              const catObj = category !== 'All' ? apiCategories.find(c => c.name === category) : null;
              const catImgUrl = catObj ? resolveProductImageUrl(catObj.image) : null;
              return (
                <button
                  key={category}
                  onClick={() => {
                    if (category === 'All') {
                      setSelectedCategory && setSelectedCategory('All');
                    } else if (catObj && onCategoryClick) {
                      onCategoryClick(catObj);
                    } else {
                      setSelectedCategory && setSelectedCategory(category);
                    }
                  }}
                  className="flex flex-col items-center gap-2 flex-shrink-0 transition-all"
                  style={{ minWidth: 88 }}
                >
                  <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-sm transition-all relative overflow-hidden"
                    style={{
                      background: isActive ? 'linear-gradient(135deg,#f05a23,#f7821a)' : `${circleColor}22`,
                      border: isActive ? '3px solid #f05a23' : `2px solid ${circleColor}55`,
                      transform: isActive ? 'scale(1.06)' : 'scale(1)',
                    }}>
                    {catImgUrl && (
                      <img
                        src={catImgUrl}
                        alt={category}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    {category === 'All'
                      ? <ShoppingCart size={30} style={{ color: isActive ? '#fff' : circleColor }} strokeWidth={2} />
                      : <span className="text-3xl leading-none">{categoryIcons[category] || '📦'}</span>
                    }
                  </div>
                  <span className="text-sm font-semibold text-center leading-tight"
                    style={{ color: isActive ? '#f05a23' : '#555', maxWidth: 88 }}>
                    {category}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* ── Fresh Fruit Deals ── */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800 font-display">🛒 Fresh Fruit Deals</h2>
            <button
              onClick={() => onOpenOffers && onOpenOffers('all')}
              className="text-sm font-semibold text-orange-500 bg-orange-50 px-3 py-1 rounded-full border border-orange-200 active:scale-95 transition-transform"
            >
              Limited Time →
            </button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            {[
              { title: ['Exotic', 'Imported', 'Fruits'], badge: 'Up to 30% Off', badgeCls: 'text-orange-600 bg-orange-100', sub: 'Pineapple · Grapes · Mango', link: 'text-orange-500', border: '#fed7aa', shadow: '0 4px 20px rgba(234,88,12,0.10)', bar: 'linear-gradient(90deg, #f59e0b, #ea580c)', img: '/uploads/categories/basket2.jpeg' },
              { title: ['Season', 'Fruits', 'Deals'], badge: 'Season Fresh', badgeCls: 'text-rose-600 bg-rose-100', sub: 'Mango · Watermelon', link: 'text-rose-500', border: '#fecdd3', shadow: '0 4px 20px rgba(244,63,94,0.10)', bar: 'linear-gradient(90deg, #f43f5e, #be185d)', img: '/uploads/categories/basket1.jpeg' },
            ].map((d, i) => (
              <div
                key={i}
                className="relative rounded-3xl overflow-hidden cursor-pointer active:scale-[0.99] transition-transform"
                style={{ background: '#fff', minHeight: 220, border: `1.5px solid ${d.border}`, boxShadow: d.shadow }}
                onClick={() => {
                  setSelectedCategory && setSelectedCategory('Fruits');
                  setTimeout(() => productsSectionRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
                }}
              >
                <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-3xl" style={{ background: d.bar }} />
                <div className="flex flex-col justify-between h-full p-6 pt-7" style={{ width: '55%' }}>
                  <div>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${d.badgeCls}`}>{d.badge}</span>
                    <h3 className="text-gray-800 font-bold text-2xl leading-tight mt-3">{d.title.join(' ')}</h3>
                    <p className="text-gray-400 text-sm mt-2">{d.sub}</p>
                  </div>
                  <span className={`text-sm font-semibold mt-4 inline-block ${d.link}`}>Shop Now →</span>
                </div>
                <div className="absolute right-0 top-0 bottom-0 z-0" style={{ width: '50%', height: '100%' }}>
                  <img
                    src={`${process.env.PUBLIC_URL}${d.img}`}
                    alt=""
                    className="absolute object-cover rounded-br-3xl"
                    style={{ right: 0, bottom: 0, width: '100%', height: '100%', objectPosition: 'center' }}
                  />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #fff 0%, transparent 45%)' }} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Feature Sections ── */}
        {Array.isArray(featureSections) && featureSections.map(section => {
          const sectionColor = section.color || '#3b82f6';
          const sectionProducts = section.products || [];
          return (
            <section key={section.id} className="mb-12">
              <div className="rounded-2xl overflow-hidden shadow-sm mb-4">
                <div
                  className="flex items-center justify-between px-5 py-4"
                  style={{ background: `linear-gradient(135deg, ${sectionColor}22 0%, ${sectionColor}08 100%)`, borderLeft: `4px solid ${sectionColor}` }}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-11 h-11 rounded-xl text-white shadow-md"
                      style={{ background: `linear-gradient(135deg, ${sectionColor}, ${sectionColor}99)` }}>
                      {section.icon ? <i className={`${section.icon} text-base`} /> : <Flame size={18} />}
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 leading-tight">{section.name}</h2>
                      <p className="text-xs text-gray-400">{sectionProducts.length} items available</p>
                    </div>
                    <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold text-white ml-2"
                      style={{ backgroundColor: sectionColor }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-white/80 motion-safe:animate-pulse inline-block" />
                      LIVE
                    </span>
                  </div>
                  <button
                    onClick={() => onOpenOffers && onOpenOffers(section.id)}
                    className="flex items-center gap-0.5 text-sm font-semibold active:scale-95 transition-transform"
                    style={{ color: sectionColor }}
                  >
                    View All <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-5">
                {sectionProducts.slice(0, 5).map((product, index) => renderProductCard(product, index))}
              </div>
            </section>
          );
        })}

        {/* ── Products Grid ── */}
        <section className="products-section" ref={productsSectionRef}>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800 font-display">
              {selectedCategory === 'All' ? 'Our Products' : selectedCategory}
            </h2>
            <span className="text-sm text-gray-500">
              {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            </span>
          </div>

          {paginatedProducts.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              <Package size={48} className="mx-auto mb-4 opacity-40" />
              <p className="text-lg font-medium">No products found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              {paginatedProducts.map((product, index) => renderProductCard(product, index))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${currentPage === 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
              >
                <ChevronLeft size={18} /> Previous
              </button>
              <div className="flex gap-2">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 rounded-lg font-medium transition-all ${currentPage === page ? 'bg-orange-500 text-white shadow-lg' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${currentPage === totalPages ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-orange-500 text-white hover:bg-orange-600'}`}
              >
                Next <ChevronRight size={18} />
              </button>
            </div>
          )}
          {totalPages > 1 && (
            <p className="text-center text-sm text-gray-500 mt-4">
              Showing {startIndex + 1}–{Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
            </p>
          )}
        </section>
      </main>

      {/* ── Shared Desktop Footer ── */}
      <DesktopFooter
        appSettings={appSettings}
        setActiveTab={navTo}
        setShowFavorites={setShowFavorites}
        setSelectedCategory={setSelectedCategory}
        onOpenOffers={onOpenOffers}
      />

      {/* Floating Checkout */}
      {cart.length > 0 && getCartCount() > 0 && (
        <button
          onClick={openCheckout}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all transform hover:scale-105 flex items-center gap-3 z-50"
        >
          <ShoppingCart size={24} />
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium">Checkout Now</span>
            <span className="text-lg font-bold">{getCartCount()} {getCartCount() === 1 ? 'item' : 'items'}</span>
            <span className="text-xs font-semibold mt-1 text-green-100 bg-green-700 bg-opacity-70 px-2 py-0.5 rounded">
              Total: ₹{typeof getCartTotal === 'function' ? getCartTotal() : cart.reduce((sum, item) => {
                const p = (item.sale_price && item.sale_price < item.price) ? item.sale_price : item.price;
                return sum + (p * item.quantity);
              }, 0)}
            </span>
          </div>
        </button>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        isOpen={showProductDetails}
        onClose={() => { setShowProductDetails(false); setSelectedProduct(null); }}
        product={selectedProduct}
        addToCart={addToCart}
        showToast={showToast}
        favorites={favorites}
        toggleFavorite={toggleFavorite}
        setShowSubscription={setShowSubscription}
        appTheme={appTheme}
      />
    </div>
  );
};

export default DesktopHomeScreen;
